/**
 * Templates Reducer
 * Manages template state
 */
import { createReducer, on } from '@ngrx/store';
import { Template, TemplateFilters, TemplateListItem, TemplateTestResponse } from '../models/template.models';
import * as TemplatesActions from './templates.actions';

export interface TemplatesState {
  // User templates
  templates: TemplateListItem[];
  templatesTotal: number;
  templatesPage: number;
  templatesPageSize: number;
  templatesTotalPages: number;
  templatesHasNext: boolean;
  templatesHasPrev: boolean;

  // Public templates
  publicTemplates: TemplateListItem[];
  publicTemplatesTotal: number;
  publicTemplatesPage: number;
  publicTemplatesPageSize: number;
  publicTemplatesTotalPages: number;
  publicTemplatesHasNext: boolean;
  publicTemplatesHasPrev: boolean;

  // Selected template
  selectedTemplate: Template | null;

  // Test results
  testResult: TemplateTestResponse | null;

  // Filters
  filters: TemplateFilters;

  // Template Selection (for API Usage)
  enabledTemplateIds: string[];
  enablingTemplates: boolean;
  disablingTemplates: boolean;

  // Loading states
  loading: boolean;
  publicTemplatesLoading: boolean;
  selectedTemplateLoading: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  cloning: boolean;
  testing: boolean;

  // Error states
  error: string | null;
  publicTemplatesError: string | null;
  selectedTemplateError: string | null;
  createError: string | null;
  updateError: string | null;
  deleteError: string | null;
  cloneError: string | null;
  testError: string | null;
  enableError: string | null;
  disableError: string | null;
}

export const initialState: TemplatesState = {
  templates: [],
  templatesTotal: 0,
  templatesPage: 1,
  templatesPageSize: 20,
  templatesTotalPages: 0,
  templatesHasNext: false,
  templatesHasPrev: false,

  publicTemplates: [],
  publicTemplatesTotal: 0,
  publicTemplatesPage: 1,
  publicTemplatesPageSize: 20,
  publicTemplatesTotalPages: 0,
  publicTemplatesHasNext: false,
  publicTemplatesHasPrev: false,

  selectedTemplate: null,
  testResult: null,
  filters: {},

  // Template Selection (for API Usage)
  enabledTemplateIds: [],
  enablingTemplates: false,
  disablingTemplates: false,

  loading: false,
  publicTemplatesLoading: false,
  selectedTemplateLoading: false,
  creating: false,
  updating: false,
  deleting: false,
  cloning: false,
  testing: false,

  error: null,
  publicTemplatesError: null,
  selectedTemplateError: null,
  createError: null,
  updateError: null,
  deleteError: null,
  cloneError: null,
  testError: null,
  enableError: null,
  disableError: null
};

export const templatesReducer = createReducer(
  initialState,
  
  // Load Templates
  on(TemplatesActions.loadTemplates, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(TemplatesActions.loadTemplatesSuccess, (state, { response }) => ({
    ...state,
    templates: response.items,
    templatesTotal: response.total,
    templatesPage: response.page,
    templatesPageSize: response.page_size,
    templatesTotalPages: response.total_pages,
    templatesHasNext: response.has_next,
    templatesHasPrev: response.has_prev,
    loading: false,
    error: null
  })),
  on(TemplatesActions.loadTemplatesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  
  // Load Public Templates
  on(TemplatesActions.loadPublicTemplates, (state) => ({
    ...state,
    publicTemplatesLoading: true,
    publicTemplatesError: null
  })),
  on(TemplatesActions.loadPublicTemplatesSuccess, (state, { response }) => ({
    ...state,
    publicTemplates: response.items,
    publicTemplatesTotal: response.total,
    publicTemplatesPage: response.page,
    publicTemplatesPageSize: response.page_size,
    publicTemplatesTotalPages: response.total_pages,
    publicTemplatesHasNext: response.has_next,
    publicTemplatesHasPrev: response.has_prev,
    publicTemplatesLoading: false,
    publicTemplatesError: null
  })),
  on(TemplatesActions.loadPublicTemplatesFailure, (state, { error }) => ({
    ...state,
    publicTemplatesLoading: false,
    publicTemplatesError: error
  })),

  // Load Single Template
  on(TemplatesActions.loadTemplate, (state) => ({
    ...state,
    selectedTemplateLoading: true,
    selectedTemplateError: null
  })),
  on(TemplatesActions.loadTemplateSuccess, (state, { template }) => ({
    ...state,
    selectedTemplate: template,
    selectedTemplateLoading: false,
    selectedTemplateError: null
  })),
  on(TemplatesActions.loadTemplateFailure, (state, { error }) => ({
    ...state,
    selectedTemplateLoading: false,
    selectedTemplateError: error
  })),

  // Create Template
  on(TemplatesActions.createTemplate, (state) => ({
    ...state,
    creating: true,
    createError: null
  })),
  on(TemplatesActions.createTemplateSuccess, (state, { template }) => ({
    ...state,
    selectedTemplate: template,
    creating: false,
    createError: null
  })),
  on(TemplatesActions.createTemplateFailure, (state, { error }) => ({
    ...state,
    creating: false,
    createError: error
  })),

  // Update Template
  on(TemplatesActions.updateTemplate, (state) => ({
    ...state,
    updating: true,
    updateError: null
  })),
  on(TemplatesActions.updateTemplateSuccess, (state, { template }) => ({
    ...state,
    selectedTemplate: template,
    templates: state.templates.map(t =>
      t.id === template.id
        ? { ...t, name: template.name, description: template.description, updated_at: template.updated_at }
        : t
    ),
    updating: false,
    updateError: null
  })),
  on(TemplatesActions.updateTemplateFailure, (state, { error }) => ({
    ...state,
    updating: false,
    updateError: error
  })),

  // Delete Template
  on(TemplatesActions.deleteTemplate, (state) => ({
    ...state,
    deleting: true,
    deleteError: null
  })),
  on(TemplatesActions.deleteTemplateSuccess, (state, { id }) => ({
    ...state,
    templates: state.templates.filter(t => t.id !== id),
    selectedTemplate: state.selectedTemplate?.id === id ? null : state.selectedTemplate,
    deleting: false,
    deleteError: null
  })),
  on(TemplatesActions.deleteTemplateFailure, (state, { error }) => ({
    ...state,
    deleting: false,
    deleteError: error
  })),

  // Clone Template
  on(TemplatesActions.cloneTemplate, (state) => ({
    ...state,
    cloning: true,
    cloneError: null
  })),
  on(TemplatesActions.cloneTemplateSuccess, (state, { template }) => ({
    ...state,
    selectedTemplate: template,
    cloning: false,
    cloneError: null
  })),
  on(TemplatesActions.cloneTemplateFailure, (state, { error }) => ({
    ...state,
    cloning: false,
    cloneError: error
  })),

  // Test Template
  on(TemplatesActions.testTemplate, (state) => ({
    ...state,
    testing: true,
    testError: null,
    testResult: null
  })),
  on(TemplatesActions.testTemplateSuccess, (state, { result }) => ({
    ...state,
    testResult: result,
    testing: false,
    testError: null
  })),
  on(TemplatesActions.testTemplateFailure, (state, { error }) => ({
    ...state,
    testing: false,
    testError: error
  })),

  // Filter Management
  on(TemplatesActions.setFilters, (state, { filters }) => ({
    ...state,
    filters
  })),
  on(TemplatesActions.clearFilters, (state) => ({
    ...state,
    filters: {}
  })),

  // UI State
  on(TemplatesActions.selectTemplate, (state, { template }) => ({
    ...state,
    selectedTemplate: template
  })),
  on(TemplatesActions.clearSelectedTemplate, (state) => ({
    ...state,
    selectedTemplate: null,
    testResult: null
  })),

  // ============================================================
  // Template Selection Reducers (for API Usage)
  // ============================================================

  // Load Enabled Templates
  on(TemplatesActions.loadEnabledTemplates, (state) => ({
    ...state,
    loading: true,
    enableError: null
  })),
  on(TemplatesActions.loadEnabledTemplatesSuccess, (state, { response }) => ({
    ...state,
    enabledTemplateIds: response.enabled_template_ids,
    loading: false,
    enableError: null
  })),
  on(TemplatesActions.loadEnabledTemplatesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    enableError: error
  })),

  // Enable Templates
  on(TemplatesActions.enableTemplates, (state) => ({
    ...state,
    enablingTemplates: true,
    enableError: null
  })),
  on(TemplatesActions.enableTemplatesSuccess, (state, { response }) => ({
    ...state,
    enabledTemplateIds: response.enabled_template_ids,
    enablingTemplates: false,
    enableError: null
  })),
  on(TemplatesActions.enableTemplatesFailure, (state, { error }) => ({
    ...state,
    enablingTemplates: false,
    enableError: error
  })),

  // Disable Templates
  on(TemplatesActions.disableTemplates, (state) => ({
    ...state,
    disablingTemplates: true,
    disableError: null
  })),
  on(TemplatesActions.disableTemplatesSuccess, (state, { response }) => ({
    ...state,
    enabledTemplateIds: response.enabled_template_ids,
    disablingTemplates: false,
    disableError: null
  })),
  on(TemplatesActions.disableTemplatesFailure, (state, { error }) => ({
    ...state,
    disablingTemplates: false,
    disableError: error
  }))
);

